/**
 * sidebarFilters.js — 사이드바 권한 필터 헬퍼 (2026-04-27 신규)
 *
 * SidebarContent.jsx 의 300줄 룰 준수를 위해 분리 + 권한 기반 메뉴 필터링 로직 통합.
 *
 * 제공 함수:
 *   - isAdminUser()                       : 현재 토큰의 role 이 admin 인지
 *   - filterMyMenus(MY_MENUS, permissions): permKey (canUse* boolean) 기반 MY_MENUS 필터
 *   - filterMenuItems(items, permCodes, isAdmin) : permCode 기반 item 필터 (permCode 없으면 admin만 노출)
 *   - filterMenuGroups(groups, permCodes, isAdmin): 그룹 단위 필터 + 빈 그룹 자동 제거
 *   - filterAdminOnlyMenus(ADMIN_ONLY_MENUS, isAdmin): admin 전용 메뉴 — admin만 노출
 *
 * 권한 모델:
 *   - admin (백엔드 getUserPermCodes 의 admin 분기) → 64개 권한 자동 보유
 *   - employee → user_permissions ∪ user_task_assignments ∪ user_feature_sets 변환분
 *   - menuConfig.js item 의 permCode 필드와 permCodes Set.has() 비교
 */

// ── 현재 사용자 role 검사 (JWT 페이로드 디코딩) ───────────────────────────────
// 위장 접속 시 sessionStorage 의 imp_token 우선, 그 외 localStorage accessToken
export function isAdminUser() {
  try {
    const token = sessionStorage.getItem('pg_imp_token') || localStorage.getItem('accessToken');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.role === 'admin';
  } catch (_) { return false; }
}

// ── MY_MENUS 권한 필터 — permKey 보유한 항목만 노출 ───────────────────────────
// permissions 객체는 usePermissions 반환 (canUseMyAttendance 등 boolean 다수)
export function filterMyMenus(MY_MENUS, permissions) {
  if (!permissions) return [];
  return MY_MENUS.filter((m) => !m.permKey || Boolean(permissions[m.permKey]));
}

// ── MENU_GROUPS items 필터 ────────────────────────────────────────────────────
// item.permCode 가 있으면 permCodes Set 에 포함된 경우만, 없으면 admin 만 노출
export function filterMenuItems(items, permCodes, isAdmin) {
  if (!items) return [];
  return items.filter((it) => {
    if (isAdmin && it.employeeOnly) return false;
    if (Array.isArray(it.permCodeAny) && it.permCodeAny.length > 0) {
      if (isAdmin) return true;
      return permCodes && it.permCodeAny.some((code) => permCodes.has(code));
    }
    if (it.permCode) {
      // permCodes Set 이 아직 로드 안 됐거나 admin 인 경우 자동 통과
      // (admin 은 백엔드 getUserPermCodes 의 admin 분기로 64개 권한 자동 보유 — 클라이언트도 동일하게 처리)
      if (isAdmin) return true;
      return permCodes && permCodes.has(it.permCode);
    }
    // permCode 미지정 item (예: 상황판) → admin 만 노출
    return isAdmin;
  });
}

// ── MENU_GROUPS 그룹 필터 + 빈 그룹 제거 ──────────────────────────────────────
// 그룹 안에 노출 item 0개면 그룹 헤더(소제목)도 안 보이게 통째로 제거.
//   (빈 그룹을 남기던 keepGroups 옵션은 "…열람" 줄과 함께 없앴다 — 2026-09-15)
export function filterMenuGroups(groups, permCodes, isAdmin) {
  if (!groups) return [];
  return groups
    .map((g) => ({ ...g, items: filterMenuItems(g.items, permCodes, isAdmin) }))
    .filter((g) => g.items.length > 0);
}

// ── ADMIN_ONLY_MENUS — admin 전용 ─────────────────────────────────────────────
// '통합 메뉴 관리' 등 권한 코드 없이 admin role 만 노출 (메뉴 자체를 다루는 메타 화면)
export function filterAdminOnlyMenus(ADMIN_ONLY_MENUS, isAdmin) {
  return isAdmin ? (ADMIN_ONLY_MENUS || []) : [];
}
